import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	override componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
		// Error logging could be added here (e.g., to an error tracking service)
	}

	handleRetry = () => {
		this.setState({ hasError: false, error: null });
	};

	handleGoHome = () => {
		window.location.href = "/";
	};

	override render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<div className="flex min-h-screen items-center justify-center p-4">
					<Card className="w-full max-w-md">
						<CardHeader className="text-center">
							<div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
								<AlertTriangle className="size-8 text-red-600" />
							</div>
							<CardTitle className="text-xl">Ha ocurrido un error</CardTitle>
							<CardDescription>
								Lo sentimos, algo salió mal. Por favor, intenta de nuevo.
							</CardDescription>
						</CardHeader>
						{this.state.error && (
							<CardContent>
								<details className="text-sm">
									<summary className="cursor-pointer text-muted-foreground hover:text-foreground">
										Ver detalles del error
									</summary>
									<pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs">
										{this.state.error.message}
									</pre>
								</details>
							</CardContent>
						)}
						<CardFooter className="flex justify-center gap-2">
							<Button variant="outline" onClick={this.handleGoHome}>
								<Home className="mr-2 size-4" />
								Ir al inicio
							</Button>
							<Button onClick={this.handleRetry}>
								<RefreshCw className="mr-2 size-4" />
								Reintentar
							</Button>
						</CardFooter>
					</Card>
				</div>
			);
		}

		return this.props.children;
	}
}

// =============================================================================
// Error Page Component (for route-level errors)
// =============================================================================

interface ErrorPageProps {
	title?: string;
	description?: string;
	showRetry?: boolean;
	showHome?: boolean;
	onRetry?: () => void;
}

export function ErrorPage({
	title = "Ha ocurrido un error",
	description = "Lo sentimos, algo salió mal. Por favor, intenta de nuevo.",
	showRetry = true,
	showHome = true,
	onRetry,
}: ErrorPageProps) {
	const handleRetry = () => {
		if (onRetry) {
			onRetry();
		} else {
			window.location.reload();
		}
	};

	const handleGoHome = () => {
		window.location.href = "/";
	};

	return (
		<div className="flex min-h-[400px] items-center justify-center p-4">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
						<AlertTriangle className="size-8 text-red-600" />
					</div>
					<CardTitle className="text-xl">{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</CardHeader>
				<CardFooter className="flex justify-center gap-2">
					{showHome && (
						<Button variant="outline" onClick={handleGoHome}>
							<Home className="mr-2 size-4" />
							Ir al inicio
						</Button>
					)}
					{showRetry && (
						<Button onClick={handleRetry}>
							<RefreshCw className="mr-2 size-4" />
							Reintentar
						</Button>
					)}
				</CardFooter>
			</Card>
		</div>
	);
}

// =============================================================================
// Query Error Component (for TanStack Query errors)
// =============================================================================

interface QueryErrorProps {
	error: Error | null;
	onRetry?: () => void;
	compact?: boolean;
}

export function QueryError({ error, onRetry, compact = false }: QueryErrorProps) {
	if (compact) {
		return (
			<div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
				<AlertTriangle className="size-4 shrink-0" />
				<span className="flex-1">{error?.message || "Error al cargar los datos"}</span>
				{onRetry && (
					<Button size="sm" variant="ghost" onClick={onRetry} className="h-auto p-1">
						<RefreshCw className="size-3" />
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
			<div className="flex size-12 items-center justify-center rounded-full bg-red-100">
				<AlertTriangle className="size-6 text-red-600" />
			</div>
			<div>
				<h3 className="font-medium">Error al cargar los datos</h3>
				<p className="text-sm text-muted-foreground">
					{error?.message || "Por favor, intenta de nuevo"}
				</p>
			</div>
			{onRetry && (
				<Button variant="outline" size="sm" onClick={onRetry}>
					<RefreshCw className="mr-2 size-4" />
					Reintentar
				</Button>
			)}
		</div>
	);
}
